import type {BookingflowStateMachine} from "../../bookingflowstatemachine";
import {State} from "../state";
import {basisActions, BasisTypeAction, basistypesActions, IGetData, BasisStateDependencies} from "./basistypes.types";
import CalendarState from "../calendarState";
import {CompanyProductPickState, NotAvailableState} from "../..";
import { Address, BasisType, CustomerType } from "@raskrask-dk/raskrask-utils";


const BASISTYPE_STATE = "BASISTYPE";
export class BasisTypeState extends State<BasisTypeAction, typeof BASISTYPE_STATE> {
  public readonly type = BASISTYPE_STATE;
  private _address: Address;
  private _basisType: BasisType | undefined;
  protected _customerType: CustomerType | undefined;

  /**
   * @param bookingflowStateMachine
   * @param address
   * The inputs below can all be set on the basis of a data fetch (rebooking/movebooking)
   * @param basisType
   * @param customerType
   */
  constructor(bookingflowStateMachine: BookingflowStateMachine, dependencies: BasisStateDependencies) {
    super(bookingflowStateMachine);
    const {address, basisType, customerType} = dependencies;
    this._basisType = basisType;
    this._customerType = customerType;
    this._address = address;
  }

  private nextState(): CalendarState | CompanyProductPickState {
    if (this.basisType === undefined && !this.customerType === undefined) throw Error("BasisType.Next must have basisType and customerType set"); // For lint. Next Action must be valid
    const calendarDeps = {
      address: this._address,
      basisType: this.basisType as BasisType,
      customerType: this.customerType as CustomerType,
    };
    return this._customerType === "Private"
      ? new CalendarState(this.bookingflowStateMachine, calendarDeps)
      : new CompanyProductPickState(this.bookingflowStateMachine, calendarDeps);
  }

  doAction(action: BasisTypeAction): BasisTypeState | CalendarState | CompanyProductPickState | NotAvailableState {
    switch (action.type) {
      case "SET_CUSTOMER_TYPE":
        this._customerType = action.data;
        return this;
      case "SET_BASISTYPE":
        this._basisType = action.data;
        return this;
      case "CHOOSE_NO_AVAILABLE":
        return new NotAvailableState(this.bookingflowStateMachine);
      case "NEXT_STATE":
        return this.nextState();
      default:
        throw new Error("This should not happen");
    }
  }

  public getActions = (): basisActions[] => basistypesActions.filter((action) => new action(this).isDoable());

  public unset = (action: BasisTypeAction): void => {
    switch (action.type) {
      case "SET_CUSTOMER_TYPE":
        this._basisType = undefined;
    }
  };

  public get basisType(): BasisType | undefined {
    return this._basisType;
  }

  public get customerType(): CustomerType | undefined {
    return this._customerType;
  }

  public getData(): IGetData {
    return {
      address: this._address,
      basisType: this.basisType,
      customerType: this.customerType,
    };
  }
}
